import { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls, GizmoHelper, GizmoViewport } from '@react-three/drei'
import * as THREE from 'three'
import { Play, Pause, SkipBack, SkipForward, Square } from 'lucide-react'
import { parseToolpath, interpolateArc, formatCycleTime, type ToolpathData, type ToolpathSegment, type ToolpathStats } from '../../lib/gcode/toolpath-parser'
import { highlightGCode } from '../../lib/gcode/highlight'
import { GeometryPreviewScene, type PlaneView } from '../shared/GeometryPreview'
import WorkOffsetPickerOverlay from '../shared/WorkOffsetPickerOverlay'
import { computeSnapPoints, cncToScene } from '../../lib/snap-points'
import type { Tool, WorkOffset, WorkOffsetRegister } from '../../types'

interface ToolpathViewerProps {
  gcode: string
  geometry?: Record<string, unknown> | null
  tools?: Tool[]
  workOffsets?: WorkOffset[]
  onWorkOffsetChange?: (offset: WorkOffset) => void
}

// ---- Tool color palette ----

const TOOL_COLORS = [
  { hex: '#06b6d4', r: 6/255, g: 182/255, b: 212/255 },   // T1: cyan
  { hex: '#3b82f6', r: 59/255, g: 130/255, b: 246/255 },   // T2: blue
  { hex: '#10b981', r: 16/255, g: 185/255, b: 129/255 },   // T3: emerald
  { hex: '#f59e0b', r: 245/255, g: 158/255, b: 11/255 },   // T4: amber
  { hex: '#8b5cf6', r: 139/255, g: 92/255, b: 246/255 },   // T5: violet
  { hex: '#f43f5e', r: 244/255, g: 63/255, b: 94/255 },    // T6: rose
  { hex: '#ec4899', r: 236/255, g: 72/255, b: 153/255 },   // T7: pink
]

function getToolColor(toolNumber: number) {
  if (toolNumber <= 0) return TOOL_COLORS[0] // fallback to cyan
  return TOOL_COLORS[(toolNumber - 1) % TOOL_COLORS.length]
}

// ---- 3D Scene sub-components ----

function ToolpathLines({ segments, visibleCount }: { segments: ToolpathSegment[]; visibleCount: number }) {
  const { rapidGeom, cutGeom } = useMemo(() => {
    const rapidPositions: number[] = []
    const cutPositions: number[] = []
    const cutColors: number[] = []

    for (let i = 0; i < segments.length && i < visibleCount; i++) {
      const seg = segments[i]
      if (seg.type === 'rapid') {
        rapidPositions.push(seg.start.x, seg.start.z, -seg.start.y)
        rapidPositions.push(seg.end.x, seg.end.z, -seg.end.y)
      } else if (seg.type === 'cut') {
        const c = getToolColor(seg.toolNumber)
        cutPositions.push(seg.start.x, seg.start.z, -seg.start.y)
        cutPositions.push(seg.end.x, seg.end.z, -seg.end.y)
        cutColors.push(c.r, c.g, c.b, c.r, c.g, c.b)
      } else if (seg.type === 'arc' && seg.center) {
        const c = getToolColor(seg.toolNumber)
        const pts = interpolateArc(seg.start, seg.end, seg.center, seg.clockwise ?? false, seg.plane)
        for (let j = 0; j < pts.length - 1; j++) {
          cutPositions.push(pts[j].x, pts[j].z, -pts[j].y)
          cutPositions.push(pts[j + 1].x, pts[j + 1].z, -pts[j + 1].y)
          cutColors.push(c.r, c.g, c.b, c.r, c.g, c.b)
        }
      }
    }

    const rapidGeom = new THREE.BufferGeometry()
    if (rapidPositions.length > 0) {
      rapidGeom.setAttribute('position', new THREE.Float32BufferAttribute(rapidPositions, 3))
    }

    const cutGeom = new THREE.BufferGeometry()
    if (cutPositions.length > 0) {
      cutGeom.setAttribute('position', new THREE.Float32BufferAttribute(cutPositions, 3))
      cutGeom.setAttribute('color', new THREE.Float32BufferAttribute(cutColors, 3))
    }

    return { rapidGeom, cutGeom }
  }, [segments, visibleCount])

  return (
    <>
      {rapidGeom.attributes.position && (
        <lineSegments geometry={rapidGeom}>
          <lineDashedMaterial color="#f97316" dashSize={1} gapSize={0.5} opacity={0.6} transparent />
        </lineSegments>
      )}
      {cutGeom.attributes.position && (
        <lineSegments geometry={cutGeom}>
          <lineBasicMaterial vertexColors opacity={0.9} transparent />
        </lineSegments>
      )}
    </>
  )
}

function ToolCylinder({
  segments,
  currentSegment,
  tools,
}: {
  segments: ToolpathSegment[]
  currentSegment: number
  tools?: Tool[]
}) {
  const groupRef = useRef<THREE.Group>(null)

  // Build tool number → diameter lookup
  const toolDiameterMap = useMemo(() => {
    const map = new Map<number, number>()
    if (tools) {
      for (const t of tools) map.set(t.number, t.diameter)
    }
    return map
  }, [tools])

  useFrame(() => {
    if (!groupRef.current || segments.length === 0) return
    const idx = Math.min(currentSegment, segments.length - 1)
    const seg = segments[idx]
    if (seg) {
      groupRef.current.position.set(seg.end.x, seg.end.z, -seg.end.y)
    }
  })

  if (segments.length === 0) return null

  const seg = segments[Math.min(currentSegment, segments.length - 1)]
  const diameter = toolDiameterMap.get(seg.toolNumber) ?? 6
  const radius = diameter / 2
  const cuttingHeight = 2
  const shaftHeight = 12
  const color = getToolColor(seg.toolNumber)

  return (
    <group ref={groupRef}>
      {/* Solid cutting end */}
      <mesh position={[0, cuttingHeight / 2, 0]}>
        <cylinderGeometry args={[radius, radius, cuttingHeight, 16]} />
        <meshStandardMaterial color={color.hex} emissive={color.hex} emissiveIntensity={0.4} />
      </mesh>
      {/* Wireframe shaft */}
      <mesh position={[0, cuttingHeight + shaftHeight / 2, 0]}>
        <cylinderGeometry args={[radius * 0.7, radius * 0.7, shaftHeight, 16]} />
        <meshBasicMaterial color={color.hex} wireframe opacity={0.3} transparent />
      </mesh>
    </group>
  )
}

function CameraSnap({
  view,
  viewKey,
  center,
  extent,
}: {
  view: PlaneView
  viewKey: number
  center: THREE.Vector3
  extent: number
}) {
  const { camera } = useThree()
  const controlsRef = useRef<any>(null)

  useEffect(() => {
    const d = extent * 1.5
    let pos: THREE.Vector3

    switch (view) {
      case 'top':
        pos = new THREE.Vector3(center.x, center.y + d, center.z)
        break
      case 'front':
        pos = new THREE.Vector3(center.x, center.y, center.z + d)
        break
      case 'right':
        pos = new THREE.Vector3(center.x + d, center.y, center.z)
        break
      case 'iso':
      default:
        pos = new THREE.Vector3(center.x + d * 0.6, center.y + d * 0.8, center.z + d * 0.6)
        break
    }

    camera.position.copy(pos)
    camera.lookAt(center)
    camera.updateProjectionMatrix()
    if (controlsRef.current) controlsRef.current.update()
  }, [view, viewKey, center, extent, camera])

  return <OrbitControls ref={controlsRef} target={center} makeDefault />
}

function MinZPlane({ stats, bounds }: { stats: ToolpathStats; bounds: { min: { x: number; y: number }; max: { x: number; y: number } } }) {
  if (stats.minZ >= 0) return null

  const dx = (bounds.max.x - bounds.min.x) * 1.2
  const dy = (bounds.max.y - bounds.min.y) * 1.2
  const cx = (bounds.min.x + bounds.max.x) / 2
  const cy = (bounds.min.y + bounds.max.y) / 2
  const sizeX = Math.max(dx, 10)
  const sizeY = Math.max(dy, 10)

  // Scene uses (x, z, -y) swap — plane Y position = G-code Z value
  return (
    <group position={[cx, stats.minZ, -cy]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[sizeX, sizeY]} />
        <meshBasicMaterial color="#f43f5e" opacity={0.06} transparent side={THREE.DoubleSide} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[sizeX, sizeY]} />
        <meshBasicMaterial color="#f43f5e" wireframe opacity={0.15} transparent />
      </mesh>
    </group>
  )
}

function Scene({
  toolpathData,
  currentSegment,
  activeView,
  viewKey,
  tools,
}: {
  toolpathData: ToolpathData
  currentSegment: number
  activeView: PlaneView
  viewKey: number
  tools?: Tool[]
}) {
  const { bounds } = toolpathData

  // Camera target: center of bounding box
  const center = useMemo(() => {
    return new THREE.Vector3(
      (bounds.min.x + bounds.max.x) / 2,
      (bounds.min.z + bounds.max.z) / 2,
      -(bounds.min.y + bounds.max.y) / 2,
    )
  }, [bounds])

  // Extent for camera distance
  const extent = useMemo(() => {
    const dx = bounds.max.x - bounds.min.x
    const dy = bounds.max.y - bounds.min.y
    const dz = bounds.max.z - bounds.min.z
    return Math.max(dx, dy, dz, 20)
  }, [bounds])

  // Request frame on state changes (frameloop="demand")
  const { invalidate } = useThree()
  useEffect(() => { invalidate() }, [toolpathData, currentSegment, activeView, viewKey, invalidate])

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[50, 80, 50]} intensity={0.6} />

      <CameraSnap view={activeView} viewKey={viewKey} center={center} extent={extent} />

      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport labelColor="white" axisHeadScale={0.8} />
      </GizmoHelper>

      {/* Grid removed — z-fighting with feature outlines */}

      <ToolpathLines segments={toolpathData.segments} visibleCount={currentSegment + 1} />
      <ToolCylinder segments={toolpathData.segments} currentSegment={currentSegment} tools={tools} />
      <MinZPlane stats={toolpathData.stats} bounds={bounds} />
    </>
  )
}

// ---- Playback controller (runs inside Canvas) ----

function PlaybackDriver({
  segmentCount,
  isPlaying,
  speed,
  currentSegmentRef,
  onSegmentChange,
}: {
  segmentCount: number
  isPlaying: boolean
  speed: number
  currentSegmentRef: React.MutableRefObject<number>
  onSegmentChange: (seg: number) => void
}) {
  const accum = useRef(0)
  const { invalidate } = useThree()

  useFrame((_, delta) => {
    if (!isPlaying || segmentCount === 0) return
    // Advance ~speed segments per second (scaled by speed multiplier)
    accum.current += delta * speed * 8
    if (accum.current >= 1) {
      const steps = Math.floor(accum.current)
      accum.current -= steps
      const next = Math.min(currentSegmentRef.current + steps, segmentCount - 1)
      if (next !== currentSegmentRef.current) {
        currentSegmentRef.current = next
        onSegmentChange(next)
      }
      if (next >= segmentCount - 1) {
        // Reached end — stop will be handled by parent via state
        onSegmentChange(segmentCount - 1)
      }
    }
    // Request next frame to keep animation loop going
    invalidate()
  })

  // Kick off render loop when playback starts
  useEffect(() => {
    if (isPlaying) invalidate()
  }, [isPlaying, invalidate])

  return null
}

// ---- Code Sidebar ----

function CodeSidebar({
  gcode,
  activeSourceLine,
  onLineClick,
}: {
  gcode: string
  activeSourceLine: number
  onLineClick: (lineNum: number) => void
}) {
  const lines = useMemo(() => gcode.split('\n'), [gcode])
  const activeLineRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to active line
  useEffect(() => {
    if (activeLineRef.current && containerRef.current) {
      const container = containerRef.current
      const el = activeLineRef.current
      const containerRect = container.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()

      if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }
  }, [activeSourceLine])

  return (
    <div className="flex flex-col h-full border-l border-cyan-500/15 bg-[#030712]/80">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-cyan-500/10 flex-shrink-0">
        <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">G-Code</span>
        <span className="font-mono text-[10px] text-slate-600">{lines.length} lines</span>
      </div>

      {/* Lines */}
      <div ref={containerRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        {lines.map((line, i) => {
          const lineNum = i + 1
          const isActive = lineNum === activeSourceLine
          return (
            <div
              key={i}
              ref={isActive ? activeLineRef : null}
              onClick={() => onLineClick(lineNum)}
              className={`flex cursor-pointer hover:bg-white/5 transition-colors duration-75 ${
                isActive ? 'bg-cyan-500/10' : ''
              }`}
            >
              <div
                className={`flex-shrink-0 w-10 text-right pr-2 font-mono text-xs leading-6 select-none ${
                  isActive ? 'text-cyan-300' : 'text-slate-700'
                }`}
              >
                {lineNum}
              </div>
              <div className="flex-1 min-w-0 font-mono text-xs leading-6 pr-2 whitespace-pre overflow-hidden text-ellipsis">
                {highlightGCode(line)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- DRO Overlay ----

function DROOverlay({
  segments,
  currentSegment,
  stats,
  cumulativeTime,
}: {
  segments: ToolpathSegment[]
  currentSegment: number
  stats: ToolpathStats
  cumulativeTime: number[]
}) {
  const seg = segments[Math.min(currentSegment, segments.length - 1)]
  if (!seg) return null

  const pos = seg.end
  const isAtMinZ = Math.abs(pos.z - stats.minZ) < 0.001
  const toolColor = getToolColor(seg.toolNumber)
  const elapsed = cumulativeTime[Math.min(currentSegment, cumulativeTime.length - 1)] ?? 0
  const total = cumulativeTime.length > 0 ? cumulativeTime[cumulativeTime.length - 1] : 0

  return (
    <div className="absolute top-3 left-3 z-10 rounded-lg border border-cyan-500/20 bg-[#030712]/80 backdrop-blur-sm px-3 py-2 font-mono text-xs pointer-events-none">
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        <span className="text-slate-600">X</span>
        <span className="text-cyan-300 tabular-nums text-right">{pos.x.toFixed(3)}</span>
        <span className="text-slate-600">Y</span>
        <span className="text-cyan-300 tabular-nums text-right">{pos.y.toFixed(3)}</span>
        <span className="text-slate-600">Z</span>
        <span className={`tabular-nums text-right ${isAtMinZ ? 'text-rose-400' : 'text-cyan-300'}`}>{pos.z.toFixed(3)}</span>

        <div className="col-span-2 border-t border-cyan-500/10 my-0.5" />

        <span className="text-slate-600">F</span>
        <span className="text-orange-400 tabular-nums text-right">{seg.feedRate > 0 ? seg.feedRate.toFixed(0) : '—'}</span>
        <span className="text-slate-600">S</span>
        <span className="text-amber-400 tabular-nums text-right">{seg.spindleSpeed > 0 ? seg.spindleSpeed.toFixed(0) : '—'}</span>
        <span className="text-slate-600">T</span>
        <span className="tabular-nums text-right flex items-center justify-end gap-1.5">
          <span style={{ color: toolColor.hex }}>T{String(seg.toolNumber).padStart(2, '0')}</span>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: toolColor.hex }} />
        </span>

        <div className="col-span-2 border-t border-cyan-500/10 my-0.5" />

        <span className="text-slate-600">Z min</span>
        <span className="text-rose-400 tabular-nums text-right">{stats.minZ.toFixed(3)}</span>
        <span className="text-slate-600">Ln</span>
        <span className="text-slate-400 tabular-nums text-right">{seg.sourceLine}</span>
        <span className="text-slate-600">Cycle</span>
        <span className="text-emerald-400 tabular-nums text-right">{formatCycleTime(stats.estimatedCycleTimeSec)}</span>
        <span className="text-slate-600">Time</span>
        <span className="text-emerald-400 tabular-nums text-right">{formatCycleTime(elapsed)} / {formatCycleTime(total)}</span>
      </div>
    </div>
  )
}

// ---- Playback Controls ----

function PlaybackControls({
  currentSegment,
  totalSegments,
  isPlaying,
  speed,
  onPlay,
  onPause,
  onStop,
  onStepBack,
  onStepForward,
  onScrub,
  onSpeedChange,
}: {
  currentSegment: number
  totalSegments: number
  isPlaying: boolean
  speed: number
  onPlay: () => void
  onPause: () => void
  onStop: () => void
  onStepBack: () => void
  onStepForward: () => void
  onScrub: (seg: number) => void
  onSpeedChange: (speed: number) => void
}) {
  const speeds = [0.25, 0.5, 1, 2, 5]

  return (
    <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-3 rounded-lg border border-cyan-500/20 bg-[#030712]/80 backdrop-blur-sm px-3 py-2">
      {/* Play/Pause */}
      <button
        onClick={isPlaying ? onPause : onPlay}
        className="flex items-center justify-center w-7 h-7 rounded-lg border border-cyan-500/25 text-cyan-300 hover:bg-cyan-500/10 transition-colors"
      >
        {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>

      {/* Stop */}
      <button
        onClick={onStop}
        className="flex items-center justify-center w-7 h-7 rounded-lg border border-cyan-500/25 text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
      >
        <Square className="w-3 h-3" />
      </button>

      {/* Step back/forward */}
      <button
        onClick={onStepBack}
        className="flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:text-cyan-300 transition-colors"
      >
        <SkipBack className="w-3 h-3" />
      </button>
      <button
        onClick={onStepForward}
        className="flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:text-cyan-300 transition-colors"
      >
        <SkipForward className="w-3 h-3" />
      </button>

      {/* Scrub bar */}
      <input
        type="range"
        min={0}
        max={Math.max(totalSegments - 1, 0)}
        value={currentSegment}
        onChange={(e) => onScrub(Number(e.target.value))}
        className="flex-1 h-1 accent-cyan-500 cursor-pointer"
      />

      {/* Segment counter */}
      <span className="font-mono text-[10px] text-slate-500 tabular-nums whitespace-nowrap min-w-[5rem] text-right">
        {(currentSegment + 1).toLocaleString()} / {totalSegments.toLocaleString()}
      </span>

      {/* Speed selector */}
      <div className="flex items-center gap-1">
        {speeds.map((s) => (
          <button
            key={s}
            onClick={() => onSpeedChange(s)}
            className={`px-1.5 py-0.5 rounded font-mono text-[10px] transition-colors ${
              speed === s
                ? 'text-cyan-300 bg-cyan-500/15 border border-cyan-500/30'
                : 'text-slate-600 hover:text-slate-400'
            }`}
          >
            {s}x
          </button>
        ))}
      </div>
    </div>
  )
}

// ---- Geometry-only mode with work offset picker ----

function GeometryOnlyView({
  geometry,
  activeView,
  viewKey,
  onViewChange,
  workOffsets,
  onWorkOffsetChange,
}: {
  geometry: Record<string, unknown>
  activeView: PlaneView
  viewKey: number
  onViewChange: (v: PlaneView) => void
  workOffsets?: WorkOffset[]
  onWorkOffsetChange?: (offset: WorkOffset) => void
}) {
  const [pickerActive, setPickerActive] = useState(true)
  const [selectedSnapId, setSelectedSnapId] = useState<string | null>(null)
  const [hoveredPos, setHoveredPos] = useState<{ x: number; y: number; z: number } | null>(null)
  const [hoveredSnapId, setHoveredSnapId] = useState<string | null>(null)
  const firstOffset = workOffsets?.[0]
  const [confirmedPos, setConfirmedPos] = useState<{ x: number; y: number; z: number } | null>(
    firstOffset ? { x: firstOffset.x, y: firstOffset.y, z: firstOffset.z } : null,
  )
  const [register, setRegister] = useState<WorkOffsetRegister>(firstOffset?.register ?? 'G54')

  const snapPoints = useMemo(() => computeSnapPoints(geometry), [geometry])

  const stock = geometry.stock_dimensions as { x?: number; y?: number; z?: number } | undefined
  const env = geometry.part_envelope as { min_x: number; min_y: number; max_x: number; max_y: number } | undefined
  const sx = stock?.x || 100
  const sy = stock?.y || 100
  const sz = stock?.z || 10
  const geoExtent = Math.max(sx, sy, sz, 20)
  const geoCamDist = geoExtent * 1.5
  const cx = env ? (env.min_x + env.max_x) / 2 : sx / 2
  const cy = env ? (env.min_y + env.max_y) / 2 : sy / 2
  const geoCenter = [cx, sz / 2, -cy] as const

  // Convert CNC positions to scene coords for the DatumOrigin
  const hoveredScenePos = useMemo<[number, number, number] | null>(
    () => hoveredPos ? cncToScene(hoveredPos, sz) : null,
    [hoveredPos, sz],
  )
  const selectedScenePos = useMemo<[number, number, number] | null>(
    () => confirmedPos ? cncToScene(confirmedPos, sz) : null,
    [confirmedPos, sz],
  )

  const handleSnapHover = useCallback((pos: { x: number; y: number; z: number } | null, snapId: string | null) => {
    setHoveredPos(pos)
    setHoveredSnapId(snapId)
  }, [])

  const handleSnapClick = useCallback((pos: { x: number; y: number; z: number }, snapId: string | null) => {
    setConfirmedPos(pos)
    setSelectedSnapId(snapId)
  }, [])

  const handleSelectChip = useCallback((id: string) => {
    const snap = snapPoints.find(p => p.id === id)
    if (!snap) return
    setSelectedSnapId(id)
    setConfirmedPos(snap.position)
  }, [snapPoints])

  const handleConfirm = useCallback(() => {
    const pos = confirmedPos ?? hoveredPos
    if (!pos || !onWorkOffsetChange) return
    onWorkOffsetChange({ register, x: pos.x, y: pos.y, z: pos.z })
    setPickerActive(false)
  }, [confirmedPos, hoveredPos, register, onWorkOffsetChange])

  const geoCanvasStyle = useMemo(() => ({ background: '#030712' }), [])
  const geoCameraConfig = useMemo(() => ({
    position: [geoCenter[0] + geoCamDist * 0.6, geoCenter[1] + geoCamDist * 0.8, geoCenter[2] + geoCamDist * 0.6] as [number, number, number],
    fov: 45,
    near: 0.1,
    far: geoExtent * 20,
  }), [geoCenter[0], geoCenter[1], geoCenter[2], geoCamDist, geoExtent])
  const handleGeoCanvasCreated = useCallback(({ gl, invalidate }: { gl: THREE.WebGLRenderer; invalidate: () => void }) => {
    const canvas = gl.domElement
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault()
      console.warn('[ToolpathViewer:Geometry] WebGL context lost — will restore')
    })
    canvas.addEventListener('webglcontextrestored', () => {
      console.log('[ToolpathViewer:Geometry] WebGL context restored')
      invalidate()
    })
  }, [])

  return (
    <div className="flex h-full w-full">
      <div className="relative flex-1 min-w-0">
        <Canvas
          frameloop="demand"
          camera={geoCameraConfig}
          style={geoCanvasStyle}
          onCreated={handleGeoCanvasCreated}
        >
          <GeometryPreviewScene
            geometry={geometry}
            activeView={activeView}
            viewKey={viewKey}
            pickerActive={pickerActive}
            snapPoints={snapPoints}
            hoveredPosition={pickerActive ? hoveredScenePos : null}
            selectedPosition={selectedScenePos}
            onSnapHover={handleSnapHover}
            onSnapClick={handleSnapClick}
            workOffsetRegister={register}
            workOffsets={workOffsets}
          />
        </Canvas>

        {/* Amber banner or picker overlay */}
        {pickerActive ? (
          <WorkOffsetPickerOverlay
            snapPoints={snapPoints}
            hoveredPosition={hoveredPos}
            selectedSnapId={selectedSnapId ?? hoveredSnapId}
            register={register}
            onSelectSnap={handleSelectChip}
            onRegisterChange={setRegister}
            onConfirm={handleConfirm}
            onClose={() => setPickerActive(false)}
          />
        ) : (
          <div className="absolute top-3 left-3 z-10 rounded-lg border border-amber-500/30 bg-amber-500/10 backdrop-blur-sm px-3 py-2 pointer-events-none">
            <span className="font-mono text-[11px] text-amber-300">Geometry preview — answer questions in chat to generate G-code</span>
          </div>
        )}

        {/* View buttons */}
        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
          {(['iso', 'top', 'front', 'right'] as const).map((v) => (
            <button
              key={v}
              onClick={() => onViewChange(v)}
              className={`
                px-2 py-1 rounded-lg font-mono text-[10px] tracking-widest uppercase
                border transition-colors duration-150
                ${activeView === v
                  ? 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30'
                  : 'text-slate-500 border-cyan-500/10 bg-[#030712]/80 backdrop-blur-sm hover:text-slate-300 hover:border-cyan-500/25'
                }
              `}
            >
              {v === 'iso' ? 'ISO' : v === 'top' ? 'TOP' : v === 'front' ? 'FRONT' : 'RIGHT'}
            </button>
          ))}
          <button
            onClick={() => setPickerActive(p => !p)}
            className={`
              mt-1 px-2 py-1 rounded-lg font-mono text-[10px] tracking-widest uppercase
              border transition-colors duration-150
              ${pickerActive
                ? 'text-amber-300 bg-amber-500/15 border-amber-500/30'
                : 'text-slate-500 border-cyan-500/10 bg-[#030712]/80 backdrop-blur-sm hover:text-slate-300 hover:border-cyan-500/25'
              }
            `}
          >
            ORIGIN
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Main Component ----

export default function ToolpathViewer({ gcode, geometry, tools, workOffsets, onWorkOffsetChange }: ToolpathViewerProps) {
  // Parse toolpath data (memoized on gcode)
  const toolpathData = useMemo(() => {
    if (!gcode) return null
    try {
      return parseToolpath(gcode)
    } catch (err) {
      console.error('Toolpath parse error:', err)
      return null
    }
  }, [gcode])

  const [currentSegment, setCurrentSegment] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [activeView, setActiveView] = useState<PlaneView>('iso')
  const [viewKey, setViewKey] = useState(0)
  const currentSegmentRef = useRef(0)

  const handleViewChange = useCallback((v: PlaneView) => {
    setActiveView(v)
    setViewKey(k => k + 1)
  }, [])

  // Keep ref in sync with state
  useEffect(() => {
    currentSegmentRef.current = currentSegment
  }, [currentSegment])

  // Reset on new gcode
  useEffect(() => {
    setCurrentSegment(0)
    currentSegmentRef.current = 0
    setIsPlaying(false)
  }, [gcode])

  const totalSegments = toolpathData?.segments.length ?? 0

  // Precompute cumulative time array (O(n) once, O(1) per frame)
  const cumulativeTime = useMemo(() => {
    if (!toolpathData) return []
    const RAPID_RATE = 15000 // mm/min typical CNC rapid traverse
    const times: number[] = []
    let cumulative = 0

    for (const seg of toolpathData.segments) {
      const dx = seg.end.x - seg.start.x
      const dy = seg.end.y - seg.start.y
      const dz = seg.end.z - seg.start.z
      let dist: number

      if (seg.type === 'arc' && seg.center) {
        dist = 0
        const pts = interpolateArc(seg.start, seg.end, seg.center, seg.clockwise ?? false, seg.plane)
        for (let j = 0; j < pts.length - 1; j++) {
          const ax = pts[j + 1].x - pts[j].x
          const ay = pts[j + 1].y - pts[j].y
          const az = pts[j + 1].z - pts[j].z
          dist += Math.sqrt(ax * ax + ay * ay + az * az)
        }
      } else {
        dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
      }

      const rate = seg.type === 'rapid' ? RAPID_RATE : (seg.feedRate > 0 ? seg.feedRate : RAPID_RATE)
      cumulative += (dist / rate) * 60 // convert mm/(mm/min) to seconds
      times.push(cumulative)
    }

    return times
  }, [toolpathData])

  const handleSegmentChange = useCallback((seg: number) => {
    setCurrentSegment(seg)
    if (seg >= totalSegments - 1) {
      setIsPlaying(false)
    }
  }, [totalSegments])

  const handlePlay = useCallback(() => {
    if (currentSegment >= totalSegments - 1) {
      setCurrentSegment(0)
      currentSegmentRef.current = 0
    }
    setIsPlaying(true)
  }, [currentSegment, totalSegments])

  const handlePause = useCallback(() => setIsPlaying(false), [])
  const handleStop = useCallback(() => {
    setIsPlaying(false)
    setCurrentSegment(0)
    currentSegmentRef.current = 0
  }, [])

  const handleStepBack = useCallback(() => {
    setIsPlaying(false)
    setCurrentSegment(prev => {
      const next = Math.max(0, prev - 1)
      currentSegmentRef.current = next
      return next
    })
  }, [])

  const handleStepForward = useCallback(() => {
    setIsPlaying(false)
    setCurrentSegment(prev => {
      const next = Math.min(totalSegments - 1, prev + 1)
      currentSegmentRef.current = next
      return next
    })
  }, [totalSegments])

  const handleScrub = useCallback((seg: number) => {
    setCurrentSegment(seg)
    currentSegmentRef.current = seg
  }, [])

  // Find source line for current segment
  const activeSourceLine = toolpathData?.segments[Math.min(currentSegment, totalSegments - 1)]?.sourceLine ?? 0

  // Click on a sidebar line → find first segment for that source line
  const handleLineClick = useCallback((lineNum: number) => {
    if (!toolpathData) return
    const idx = toolpathData.segments.findIndex(s => s.sourceLine >= lineNum)
    if (idx !== -1) {
      setCurrentSegment(idx)
      currentSegmentRef.current = idx
      setIsPlaying(false)
    }
  }, [toolpathData])

  if (!gcode && !geometry) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="font-mono text-xs text-slate-600">No data to visualize</span>
      </div>
    )
  }

  if (!gcode && geometry) {
    // Geometry-only preview (interrogation mode — no G-code yet)
    return (
      <GeometryOnlyView
        geometry={geometry}
        activeView={activeView}
        viewKey={viewKey}
        onViewChange={handleViewChange}
        workOffsets={workOffsets}
        onWorkOffsetChange={onWorkOffsetChange}
      />
    )
  }

  if (!toolpathData || toolpathData.segments.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="font-mono text-xs text-slate-600">No toolpath segments found</span>
      </div>
    )
  }

  // Calculate camera distance based on bounds
  const { bounds } = toolpathData
  const extent = Math.max(
    bounds.max.x - bounds.min.x,
    bounds.max.y - bounds.min.y,
    bounds.max.z - bounds.min.z,
    20,
  )
  const camDist = extent * 1.5
  const center = [
    (bounds.min.x + bounds.max.x) / 2,
    (bounds.min.z + bounds.max.z) / 2,
    -(bounds.min.y + bounds.max.y) / 2,
  ] as const

  const canvasStyle = useMemo(() => ({ background: '#030712' }), [])
  const cameraConfig = useMemo(() => ({
    position: [center[0] + camDist * 0.6, center[1] + camDist * 0.8, center[2] + camDist * 0.6] as [number, number, number],
    fov: 45,
    near: 0.1,
    far: extent * 20,
  }), [center[0], center[1], center[2], camDist, extent])
  const handleCanvasCreated = useCallback(({ gl, invalidate }: { gl: THREE.WebGLRenderer; invalidate: () => void }) => {
    const canvas = gl.domElement
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault()
      console.warn('[ToolpathViewer] WebGL context lost — will restore')
    })
    canvas.addEventListener('webglcontextrestored', () => {
      console.log('[ToolpathViewer] WebGL context restored')
      invalidate()
    })
  }, [])

  return (
    <div className="flex h-full w-full">
      {/* 3D Viewport */}
      <div className="relative flex-[3] min-w-0">
        <Canvas
          frameloop="demand"
          camera={cameraConfig}
          style={canvasStyle}
          onCreated={handleCanvasCreated}
        >
          <Scene
            toolpathData={toolpathData}
            currentSegment={currentSegment}
            activeView={activeView}
            viewKey={viewKey}
            tools={tools}
          />
          <PlaybackDriver
            segmentCount={totalSegments}
            isPlaying={isPlaying}
            speed={speed}
            currentSegmentRef={currentSegmentRef}
            onSegmentChange={handleSegmentChange}
          />
        </Canvas>

        {/* DRO Overlay */}
        <DROOverlay segments={toolpathData.segments} currentSegment={currentSegment} stats={toolpathData.stats} cumulativeTime={cumulativeTime} />

        {/* Plane View Buttons */}
        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
          {(['iso', 'top', 'front', 'right'] as const).map((v) => (
            <button
              key={v}
              onClick={() => handleViewChange(v)}
              className={`
                px-2 py-1 rounded-lg font-mono text-[10px] tracking-widest uppercase
                border transition-colors duration-150
                ${activeView === v
                  ? 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30'
                  : 'text-slate-500 border-cyan-500/10 bg-[#030712]/80 backdrop-blur-sm hover:text-slate-300 hover:border-cyan-500/25'
                }
              `}
            >
              {v === 'iso' ? 'ISO' : v === 'top' ? 'TOP' : v === 'front' ? 'FRONT' : 'RIGHT'}
            </button>
          ))}
        </div>

        {/* Playback Controls */}
        <PlaybackControls
          currentSegment={currentSegment}
          totalSegments={totalSegments}
          isPlaying={isPlaying}
          speed={speed}
          onPlay={handlePlay}
          onPause={handlePause}
          onStop={handleStop}
          onStepBack={handleStepBack}
          onStepForward={handleStepForward}
          onScrub={handleScrub}
          onSpeedChange={setSpeed}
        />
      </div>

      {/* Code Sidebar */}
      <div className="flex-1 min-w-0 max-w-xs">
        <CodeSidebar
          gcode={gcode}
          activeSourceLine={activeSourceLine}
          onLineClick={handleLineClick}
        />
      </div>
    </div>
  )
}
